import type {
  GetState,
  Dispatch,
  ThunkAction,
  ThunkSyncAction,
} from 'src/reducers';
import type {
  BroadcastSuppressionPhoneDetails,
  TimeFrame,
} from 'src/components/messaging/broadcast-suppression/types';

import type {ApiBroadcast, BroadcastScheduleType} from 'src/types/broadcasts';
import type {
  SetOfDiscoverOrTrmCandidateDetails,
  NumbersWithNames,
  MsgExtLocationDetails,
  TypeaheadResults,
  TwilioPhoneType,
} from 'src/types/draft-messages';
import type {
  Contact,
  SuggestionType,
  ThreadListSuggestion,
} from 'src/types/contacts';

import _get from 'lodash/get';
import invariant from 'invariant';

import {getPrimaryPhone} from 'src/utils/contacts';
import {resolveExactMatches} from 'src/components/messaging/typeahead/util';
import {validateBroadcast} from 'src/utils/broadcasts';
import {createBroadcast} from 'src/action-creators/broadcasts';

import {selectAllowedMediaFilenameRegex} from 'src/selectors/chat';

import {
  selectFilesForUpload,
  selectFileUploadErrors,
} from 'src/selectors/draft-messages';
import * as reduxApi from 'src/utils/redux-api-v2';
import {camel, snake} from 'src/utils';
import {selectDefaultPhoneNumberForPhoneInbox} from 'src/selectors/phone-number-sets';
import {getCurrentAgent} from 'src/selectors/accounts';
import type {AtsContact} from 'src/chat-extension/messages';
import type {AudienceMember} from 'src/types/audience-member';


export const SET_GRABBED_PHONE_NUMBERS = 'draftMessages/setGrabbedPhoneNumbers';
export const MSG_EXT_LOCATION_DETAILS = 'draftMessages/msgExtLocationDetails';
export const SELECT_PHONE = 'draftMessages/selectPhone';
export const SET_PHONE_FOR_NEW = 'draftMessages/setPhoneForNew';
export const INPUT_PHONE = 'draftMessages/inputPhone';
export const SET_MULTIPLE_RECIPIENTS = 'draftMessages/setMultipleRecipients';
export const SET_DISCOVER_OR_TRM_ORIGIN_AND_METADATA =
  'draftMessages/setDiscoverOrTrmOriginAndMetadata';
export const SET_DISCOVER_OR_TRM_IFRAME_CONTEXT =
  'draftMessages/setDiscoverOrTrmIframeContext';
export const CHANGE_DRAFT = 'draftMessages/change';
export const CLEAR_DRAFT = 'draftMessages/clear';
export const ADD_SCHEDULE = 'draftMessages/addSchedule';
export const CLEAR_NEW_MESSAGE = 'draftMessages/clearNewMessage';
export const SET_SCHEDULE_TYPE = 'draftMessages/setScheduleType';
export const UPDATE_SCHEDULED_DRAFT = 'draftMessages/updateDraft';
export const SET_SCHEDULED_DRAFT = 'draftMessages/setDraft';
export const SET_PHONE_TYPES = 'draftMessages/setPhoneTypes';
export const CLEAR_PHONE_TYPES = 'draftMessages/clearPhoneTypes';
export const CLEAR_RESTORED_PHONE_NUMBERS =
  'draftMessages/clearRestoredPhoneNumbers';
export const SET_RESTORED_PHONE_NUMBERS =
  'draftMessages/setRestoredPhoneNumbers';
export const SET_RESTORED_LANDLINE_NUMBERS =
  'draftMessages/setRestoredLandlineNumbers';
export const SET_RESTORED_ACTIVE_PLACEMENT_NUMBERS =
  'draftMessages/setRestoredActivePlacementNumbers';
export const REMOVE_NUMBER_FROM_RESTORED_PHONE_NUMBERS =
  'draftMessages/removeNumberRestoredPhoneNumbers';
export const SET_BROADCAST_SUPPRESSION_TIMEFRAME =
  'draftMessages/setBroadcastSuppressionTimeFrame';
export const CLEAR_BROADCAST_SUPPRESSION_TIMEFRAME =
  'draftMessages/clearBroadcastSuppressionTimeFrame';
export const SET_CURRENTLY_APPLIED_SUPPRESSION =
  'draftMessages/setCurrentlyAppliedSuppression';
export const SET_GRAB_NUMBERS_SCRAPED_HTML =
  'draftMessages/setGrabNumbersScrapedHtml';
export const SET_GRAB_NUMBERS_WITH_NAMES =
  'draftMessages/setGrabNumbersWithNames';
export const SET_URL_STATE = 'draftMessages/setUrlState';
export const ADD_FILES: 'chat/file-upload/add-files' =
  'chat/file-upload/add-files';
export const CLEAR_FILES: 'chat/file-upload/clear-files' =
  'chat/file-upload/clear-files';
export const REMOVE_FILE: 'chat/file-upload/remove-file' =
  'chat/file-upload/remove-file';
export const REMOVE_FILE_ERROR: 'chat/file-upload/remove-error' =
  'chat/file-upload/remove-error';

export const UPDATE_FIRST_RECIPIENT_FULL_NAME =
  'draftMessages/updateFirstRecipientFullName';
export const ERROR_FILE_SIZE = 'error-file-size';
export const ERROR_FILE_TYPE = 'error-file-type';
export const ERROR_FILE_COUNT = 'error-file-count';

export type Action =
  | InputPhoneAction
  | SetMultipleRecipientsAction
  | ChangeDraftAction
  | ClearDraftAction
  | AddScheduleAction
  | ClearNewMessageAction
  | SetScheduleTypeAction
  | UpdateDraftAction
  | ClearFilesAction
  | RemoveFileAction
  | RemoveFileUploadErrorAction
  | SetPhoneTypesAction
  | ClearPhoneTypesAction
  | SetRestoredPhoneNumbersAction
  | SetRestoredLandlineNumbersAction
  | RemoveNumberFromRestoredPhoneNumbersAction
  | SetBroadcastSuppressionTimeFrameAction
  | ClearBroadcastSuppressionTimeFrameAction
  | ClearRestoredPhoneNumbersAction
  | SetUrlStateAction
  | SetMsgExtLocationDataAction
  | SetCurrentlyAppliedSuppressionAction
  | SetGrabNumbersScrapeHtmlAction
  | SetGrabNumbersWithNamesAction
  | SetGrabbedPhoneNumbersAction
  | UpdateFirstRecipientFullNameAction
  | SetRestoredActivePlacementNumbersAction;

type UpdateFirstRecipientFullNameAction = {
  type: typeof UPDATE_FIRST_RECIPIENT_FULL_NAME;
  payload: {
    fullName: string;
    phone: string;
  };
};

export const updateFirstRecipientFullName = (
  fullName: string,
  phone: string,
): UpdateFirstRecipientFullNameAction => ({
  type: UPDATE_FIRST_RECIPIENT_FULL_NAME,
  payload: {fullName, phone},
});

type SetGrabbedPhoneNumbersAction = {
  type: typeof SET_GRABBED_PHONE_NUMBERS;
  payload: {
    value: {atsContact: AtsContact}[];
  };
};

export const setGrabbedPhoneNumbersActionEvent = (
  value: {atsContact: AtsContact}[],
): SetGrabbedPhoneNumbersAction => ({
  type: SET_GRABBED_PHONE_NUMBERS,
  payload: {value},
});

type InputPhoneAction = {
  type: 'draftMessages/inputPhone';
  payload: string | null | undefined;
};

export const inputPhone = (phone?: string | null): InputPhoneAction => ({
  type: INPUT_PHONE,
  payload: phone,
});

type ChangeDraftAction = {
  type: 'draftMessages/change';
  payload: {
    to: string;
    body: string;
  };
};

export const setMultipleRecipients = (
  recipients: Array<SuggestionType | string>,
): SetMultipleRecipientsAction => ({
  type: SET_MULTIPLE_RECIPIENTS,
  payload: recipients,
});

type SetMultipleRecipientsAction = {
  type: 'draftMessages/setMultipleRecipients';
  payload: Array<SuggestionType | string>;
};

export const setDiscoverOrTrmOriginAndMetadata = (
  origin: string,
  candidateDetails: SetOfDiscoverOrTrmCandidateDetails,
): SetDiscoverOrTrmOriginAndMetadataAction => ({
  type: SET_DISCOVER_OR_TRM_ORIGIN_AND_METADATA,
  payload: {origin, candidateDetails},
});

type SetDiscoverOrTrmOriginAndMetadataAction = {
  type: typeof SET_DISCOVER_OR_TRM_ORIGIN_AND_METADATA;
  payload: {
    origin: string;
    candidateDetails: SetOfDiscoverOrTrmCandidateDetails;
  };
};

export const setDiscoverOrTrmIframeContext = (
  payload: boolean,
): SetDiscoverOrTrmIframeContextAction => ({
  type: SET_DISCOVER_OR_TRM_IFRAME_CONTEXT,
  payload,
});

type SetDiscoverOrTrmIframeContextAction = {
  type: typeof SET_DISCOVER_OR_TRM_IFRAME_CONTEXT;
  payload: boolean;
};

export const changeDraft = (
  to: string,
  nextDraft: string,
): ChangeDraftAction => ({
  type: CHANGE_DRAFT,
  payload: {
    to,
    body: nextDraft,
  },
});

type ClearDraftAction = {
  type: 'draftMessages/clear';
  payload: string;
};

export const clearDraft = (to: string): ClearDraftAction => ({
  type: CLEAR_DRAFT,
  payload: to,
});

type ClearNewMessageAction = {
  type: 'draftMessages/clearNewMessage';
};

export const clearNewMessage = (): ClearNewMessageAction => ({
  type: CLEAR_NEW_MESSAGE,
});

type SetMsgExtLocationDataAction = {
  type: typeof MSG_EXT_LOCATION_DETAILS;
  payload: MsgExtLocationDetails;
};

export const setMsgExtLocationData = (
  payload: MsgExtLocationDetails,
): SetMsgExtLocationDataAction => ({
  type: MSG_EXT_LOCATION_DETAILS,
  payload,
});

type AddScheduleAction = {
  type: 'draftMessages/addSchedule';
  payload: string;
  meta: {
    toPhone: string;
  };
};

export const addSchedule = (
  toPhone: string,
  date: string,
): AddScheduleAction => ({
  type: ADD_SCHEDULE,
  payload: date,
  meta: {
    toPhone,
  },
});

type SetScheduleTypeAction = {
  type: 'draftMessages/setScheduleType';
  payload: {
    toPhone: string;
    scheduleType: BroadcastScheduleType;
    sendDate: string;
  };
};

export const setScheduleType = (
  toPhone: any,
  scheduleType: any,
  sendDate: any,
): SetScheduleTypeAction => ({
  type: SET_SCHEDULE_TYPE,
  payload: {
    toPhone,
    scheduleType,
    sendDate,
  },
});

type UpdateDraftAction = {
  type: 'draftMessages/updateDraft';
  payload: {
    draftKey: string;
    draft: Record<any, any>;
  };
};

// This literally splats the draft over the prior draft for given draftKey
export const updateDraft = (
  draftKey: string,
  draft: Record<any, any>,
): UpdateDraftAction => ({
  type: UPDATE_SCHEDULED_DRAFT,
  payload: {
    draftKey,
    draft,
  },
});

type SetDraftAction = {
  type: 'draftMessages/setDraft';
  payload: {
    draftKey: string;
    draft: Record<any, any>;
  };
};

/*
This is different from updateDraft, it replaces the draft and gets a new id
this is needed because DumbMessageCompose will not rerender if the id does not change
use setDraft when you want to create a new draft even when the user is focusing on compose.
*/
export const setDraft = (
  draftKey: string,
  draft: Record<any, any>,
): SetDraftAction => ({
  type: SET_SCHEDULED_DRAFT,
  payload: {
    draftKey,
    draft,
  },
});

export const sendDraftBroadcast =
  (
    draftKey: string,
    ignorePhoneNumberList: Array<string> = [],
  ): ThunkAction<ApiBroadcast | null | undefined> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const {recipients, drafts, applySuppression} = getState().draftMessages;
    const scheduledMessage = drafts[draftKey];

    invariant(scheduledMessage, 'There is no message for this draftKey');

    let broadcast = {
      body: scheduledMessage.body,
      dripMessage: scheduledMessage.dripMessage,
      templateId: scheduledMessage.templateId,
      schedulerTemplateId: scheduledMessage.schedulerTemplateId,
      applySuppression,
    };

    if (scheduledMessage.sendDate) {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sendDate' does not exist in type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
      broadcast = {...broadcast, sendDate: scheduledMessage.sendDate};
    } else {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'sendNow' does not exist in type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
      broadcast = {...broadcast, sendNow: true};
    }

    invariant(recipients.length > 0, 'Must have at least 1 recipient');

    const firstRecipient = recipients[0];
    if (
      typeof firstRecipient === 'object' &&
      //@ts-expect-error
      firstRecipient.threadListSuggestion
    ) {
      broadcast = {
        ...broadcast,
        // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'threadListId' does not exist in type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
        threadListId: firstRecipient.threadListSuggestion.id,
      };
    } else {
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'phoneNumbers' does not exist in type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
      broadcast = {...broadcast, phoneNumbers: []};
      for (const recipient of recipients) {
        if (typeof recipient === 'string') {
          // @ts-expect-error - TS2339 - Property 'phoneNumbers' does not exist on type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
          broadcast.phoneNumbers.push(recipient);
          //@ts-expect-error
        } else if (recipient.atsContact) {
          // TODO (kyle): should server handle external contacts?
          // @ts-expect-error - TS2339 - Property 'phoneNumbers' does not exist on type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
          broadcast.phoneNumbers.push(recipient.atsContact.phoneNumber);
          //@ts-expect-error
        } else if (recipient.audienceMember) {
          // @ts-expect-error - TS2339 - Property 'phoneNumbers' does not exist on type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
          broadcast.phoneNumbers.push(recipient.audienceMember.phoneNumber);
        } else {
          const contactPhoneNumber =
            //@ts-expect-error
            recipient.contact && getPrimaryPhone(recipient.contact);
          if (contactPhoneNumber) {
            // @ts-expect-error - TS2339 - Property 'phoneNumbers' does not exist on type '{ body: any; dripMessage: any; templateId: any; schedulerTemplateId: any; applySuppression: any; }'.
            broadcast.phoneNumbers.push(contactPhoneNumber);
          }
        }
      }
    }

    const errors = validateBroadcast(broadcast, recipients.length);
    if (errors) {
      throw errors;
    }

    dispatch(clearDraft(draftKey));
    try {
      const response = await dispatch(
        //@ts-expect-error
        createBroadcast(broadcast, ignorePhoneNumberList),
      );
      return response;
    } catch (error: any) {
      // reinsert the draft and throw an error (to trigger a modal)
      dispatch(updateDraft(draftKey, scheduledMessage));
      throw error;
    }
  };

function addError(errors: Array<File> | Array<File | string>, error: string) {
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'File'.
  if (!errors.includes(error)) {
    return [...errors, error];
  }

  return errors;
}

export const addAndValidateFiles =
  (draftKey: string, files: File[]): ThunkSyncAction =>
  (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    const prevFiles = selectFilesForUpload(state, draftKey);
    const prevErrors = selectFileUploadErrors(state, draftKey);
    const fileTypeRegex = selectAllowedMediaFilenameRegex(state);

    const nextFiles = [...prevFiles];
    let nextErrors = prevErrors;

    for (const file of files) {
      if (nextFiles.length >= 5) {
        // @ts-expect-error - TS2322 - Type '(string | File)[]' is not assignable to type 'File[]'.
        nextErrors = addError(nextErrors, ERROR_FILE_COUNT);
        break;
      }
      if (file && !nextFiles.includes(file)) {
        if (file.size >= 5000000) {
          // @ts-expect-error - TS2322 - Type '(string | File)[]' is not assignable to type 'File[]'.
          nextErrors = addError(nextErrors, ERROR_FILE_SIZE);
        } else if (!fileTypeRegex.test(file.name)) {
          // @ts-expect-error - TS2322 - Type '(string | File)[]' is not assignable to type 'File[]'.
          nextErrors = addError(nextErrors, ERROR_FILE_TYPE);
        } else {
          nextFiles.push(file);
        }
      }
    }

    dispatch(updateDraft(draftKey, {files: nextFiles, fileErrors: nextErrors}));
  };

type RemoveFileUploadErrorAction = {
  type: 'chat/file-upload/remove-error';
  payload: {
    draftKey: string;
    error: string;
  };
};

export const removeFileUploadError = (
  draftKey: string,
  error: string,
): RemoveFileUploadErrorAction => ({
  type: REMOVE_FILE_ERROR,
  payload: {draftKey, error},
});

type ClearFilesAction = {
  type: 'chat/file-upload/clear-files';
  payload: {
    draftKey: string;
  };
};

export const clearFiles = (draftKey: string): ClearFilesAction => ({
  type: CLEAR_FILES,
  payload: {draftKey},
});

type RemoveFileAction = {
  type: 'chat/file-upload/remove-file';
  payload: {
    draftKey: string;
    file: File;
  };
};

export const removeFile = (draftKey: string, file: File): RemoveFileAction => ({
  type: REMOVE_FILE,
  payload: {draftKey, file},
});

type SetPhoneTypesAction = {
  type: 'draftMessages/setPhoneTypes';
  payload: {
    value: BroadcastSuppressionPhoneDetails[];
  };
};

export const setPhoneTypesEvent = (
  value: BroadcastSuppressionPhoneDetails[],
): SetPhoneTypesAction => ({
  type: SET_PHONE_TYPES,
  payload: {value},
});

type ClearPhoneTypesAction = {
  type: 'draftMessages/clearPhoneTypes';
};

export const clearPhoneTypesEvent = (): ClearPhoneTypesAction => ({
  type: CLEAR_PHONE_TYPES,
});

type ClearRestoredPhoneNumbersAction = {
  type: 'draftMessages/clearRestoredPhoneNumbers';
};

export const clearRestoredPhoneNumbersEvent =
  (): ClearRestoredPhoneNumbersAction => ({
    type: CLEAR_RESTORED_PHONE_NUMBERS,
  });

type SetRestoredPhoneNumbersAction = {
  type: 'draftMessages/setRestoredPhoneNumbers';
  payload: {
    value: Array<string>;
  };
};

export const setRestoredPhoneNumbersEvent = (
  value: Array<string>,
): SetRestoredPhoneNumbersAction => ({
  type: SET_RESTORED_PHONE_NUMBERS,
  payload: {value},
});

export const setRestoredActivePlacementNumbersEvent = (
  value: Array<string>,
): SetRestoredActivePlacementNumbersAction => ({
  type: SET_RESTORED_ACTIVE_PLACEMENT_NUMBERS,
  payload: {value},
});

type SetRestoredActivePlacementNumbersAction = {
  type: 'draftMessages/setRestoredActivePlacementNumbers';
  payload: {
    value: Array<string>;
  };
};

type SetRestoredLandlineNumbersAction = {
  type: 'draftMessages/setRestoredLandlineNumbers';
  payload: {
    value: Array<string>;
  };
};

export const setRestoredLandlineNumbersEvent = (
  value: Array<string>,
): SetRestoredLandlineNumbersAction => ({
  type: SET_RESTORED_LANDLINE_NUMBERS,
  payload: {value},
});

type RemoveNumberFromRestoredPhoneNumbersAction = {
  type: 'draftMessages/removeNumberRestoredPhoneNumbers';
  payload: {
    value: Array<string>;
  };
};

export const removeNumberFromRestoredPhoneNumbersEvent = (
  value: Array<string>,
): RemoveNumberFromRestoredPhoneNumbersAction => ({
  type: REMOVE_NUMBER_FROM_RESTORED_PHONE_NUMBERS,
  payload: {value},
});

type SetBroadcastSuppressionTimeFrameAction = {
  type: 'draftMessages/setBroadcastSuppressionTimeFrame';
  payload: {
    value: TimeFrame;
  };
};

export const setBroadcastSuppressionTimeFrameEvent = (
  value: TimeFrame,
): SetBroadcastSuppressionTimeFrameAction => ({
  type: SET_BROADCAST_SUPPRESSION_TIMEFRAME,
  payload: {value},
});

type SetGrabNumbersScrapeHtmlAction = {
  type: 'draftMessages/setGrabNumbersScrapedHtml';
  payload: {
    value: string;
  };
};

export const setGrabNumbersScrapeHtmlActionEvent = (
  value: string,
): SetGrabNumbersScrapeHtmlAction => ({
  type: SET_GRAB_NUMBERS_SCRAPED_HTML,
  payload: {value},
});

type SetGrabNumbersWithNamesAction = {
  type: 'draftMessages/setGrabNumbersWithNames';
  payload: {
    value: NumbersWithNames[];
  };
};

export const setGrabNumbersWithNamesActionEvent = (
  value: NumbersWithNames[],
): SetGrabNumbersWithNamesAction => ({
  type: SET_GRAB_NUMBERS_WITH_NAMES,
  payload: {value},
});

type ClearBroadcastSuppressionTimeFrameAction = {
  type: 'draftMessages/clearBroadcastSuppressionTimeFrame';
};

export const clearBroadcastSuppressionTimeFrameEvent =
  (): ClearBroadcastSuppressionTimeFrameAction => ({
    type: CLEAR_BROADCAST_SUPPRESSION_TIMEFRAME,
  });

type SetUrlStateAction = {
  type: typeof SET_URL_STATE;
  payload: {
    draftKey: string;
    detectedUrls: Set<string>;
    bannerVisible: boolean;
    shouldShortenLinks?: boolean;
  };
};

export const setUrlState = (
  draftKey: string,
  detectedUrls: Set<string>,
  bannerVisible: boolean,
  shouldShortenLinks?: boolean,
): SetUrlStateAction => ({
  type: SET_URL_STATE,
  payload: {
    draftKey,
    detectedUrls,
    bannerVisible,
    shouldShortenLinks,
  },
});

type SetCurrentlyAppliedSuppressionAction = {
  type: 'draftMessages/setCurrentlyAppliedSuppression';
  payload: {
    value: boolean;
  };
};

export const setCurrentlyAppliedSuppression = (
  value: boolean,
): SetCurrentlyAppliedSuppressionAction => ({
  type: SET_CURRENTLY_APPLIED_SUPPRESSION,
  payload: {value},
});

// Typeahead API call
export const fetchTypeaheadData =
  (query: string): ThunkAction<TypeaheadResults> =>
  async (dispatch: Dispatch): Promise<TypeaheadResults> => {
    try {
      const response = await dispatch(
        reduxApi.get(
          'messages_v2/typeahead',
          {
            limit: 10,
            offset: 0,
            search_string: query,
            should_include_lists: true,
          },
          {
            apiPath: '/api/v2/',
          },
        ),
      );
      const typeaheadData = camel(response);
      // format the data as needed
      // for contacts, {contact: contactObj}, {audienceMember: audienceMemberObj }, {threadListSuggestion: threadListSuggestionObj}
      const audienceMembers = typeaheadData.entitiesData.entities.map(
        (entity: AudienceMember) => ({
          audienceMember: {...entity, phoneNumber: entity.primaryPhone || ''},
        }),
      );
      const contacts = typeaheadData.contactData.map((contact: Contact) => ({
        contact,
      }));
      const contactLists = typeaheadData.contactListData.contactLists.map(
        (threadListSuggestion: ThreadListSuggestion) => ({
          threadListSuggestion,
        }),
      );
      const exactMatches = resolveExactMatches(
        typeaheadData.exactMatches ?? [],
        contacts,
        audienceMembers,
      );
      return {contacts, audienceMembers, contactLists, exactMatches};
    } catch (err) {
      throw err;
    }
  };

export const getDetailsFromPhoneNumberLookup =
  (phoneNumbers: string[]): ThunkAction<TwilioPhoneType[]> =>
  async (dispatch: Dispatch, getState: GetState) => {
    const currentSensePhone = selectDefaultPhoneNumberForPhoneInbox(getState());
    const {type, time} =
      getCurrentAgent(getState())?.securityRole?.configurations
        ?.broadcastSuppression || {};
    const config =
      type !== 'off' &&
      time &&
      typeof time === 'object' &&
      Object.keys(time).length > 0
        ? time
        : undefined;
    try {
      const phoneNumberDetails = await dispatch(
        reduxApi.post(
          `phone-number-lookup`,
          snake({
            phoneNumbers,
            sensePhone: currentSensePhone,
            broadcastSuppressionSetting: config,
          }),
        ),
      );
      return phoneNumberDetails;
    } catch (_) {
      return [];
    }
  };

export const getSuppressionDetailsForList =
  (contactListId: string): ThunkAction<TwilioPhoneType[]> =>
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      const currentSensePhone = selectDefaultPhoneNumberForPhoneInbox(
        getState(),
      );
      const {type, time} =
        getCurrentAgent(getState())?.securityRole?.configurations
          ?.broadcastSuppression || {};
      if (type !== 'off' && contactListId) {
        const result = await dispatch(
          reduxApi.post(
            'messages_v2/broadcasts/suppression',
            {
              contact_list_ids: [contactListId],
              file_ids: [],
              grabbed_phone_numbers: [],
              broadcast_suppression_setting: {
                number: time ? Number(time.number) : undefined,
                unit: time ? time.unit : undefined,
              },
              sense_phone: currentSensePhone,
            },
            {},
            {
              apiPath: '/api/v2/',
            },
          ),
        );
        return camel(result);
      }
      return [];
    } catch (err: any) {
      throw err;
    }
  };
